<script setup lang="ts">
import { computed } from 'vue';

// A custom pill button styled entirely with Tailwind `tw3-primary-*`/`tw3-surface-*`
// utilities, so the page's inline theme color applies to it (unlike PrimeVue
// buttons, whose --p-primary-* tokens are anchored to :root and don't inherit
// the scoped override).
const props = withDefaults(
	defineProps<{
		label?: string;
		icon?: string;
		iconPos?: 'left' | 'right';
		// primary = solid accent, secondary = outlined, text = borderless ghost.
		variant?: 'primary' | 'secondary' | 'text';
		size?: 'normal' | 'small';
		disabled?: boolean;
		loading?: boolean;
	}>(),
	{
		label: '',
		icon: '',
		iconPos: 'left',
		variant: 'primary',
		size: 'normal',
		disabled: false,
		loading: false
	}
);

const variantClass = computed(() => {
	switch (props.variant) {
		case 'secondary':
			return 'tw3-border tw3-border-surface-300 tw3-bg-surface-0 tw3-text-surface-700 hover:tw3-bg-surface-100';
		case 'text':
			return 'tw3-text-surface-600 hover:tw3-bg-surface-100';
		default:
			return 'tw3-bg-primary-600 tw3-text-white tw3-shadow-sm hover:tw3-bg-primary-700';
	}
});

const sizeClass = computed(() =>
	props.size === 'small'
		? 'tw3-px-3.5 tw3-py-2 tw3-text-xs'
		: 'tw3-px-5 tw3-py-2.5 tw3-text-sm'
);

const isDisabled = computed(() => props.disabled || props.loading);
</script>

<template>
	<button
		class="tw3-inline-flex tw3-items-center tw3-justify-center tw3-gap-2 tw3-rounded-full tw3-font-semibold tw3-transition-all focus:tw3-outline-none disabled:tw3-pointer-events-none disabled:tw3-opacity-60"
		:class="[variantClass, sizeClass]"
		:disabled="isDisabled"
		type="button"
	>
		<i v-if="loading" class="pi pi-spinner pi-spin" />
		<i v-else-if="icon && iconPos === 'left'" :class="icon" />
		<span v-if="label">{{ label }}</span>
		<i v-if="!loading && icon && iconPos === 'right'" :class="icon" />
	</button>
</template>
